// App.jsx - Version corrigée (Hooks toujours appelés)
import React, { useEffect, useCallback } from 'react';
import { BrowserRouter as Router, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthProvider';
import { useAuth } from './hooks/useAuth';
import AppRoutes from './routes';
import { IdleTimerProvider } from './contexts/IdleTimerProvider';
import { SessionExpiredModal } from './components/SessionExpiredModal';
import { useIdleTimer } from './hooks/useIdleTimer';
import { MenuProvider } from './contexts/MenuProvider';

// ✅ Routes publiques qui ne nécessitent pas d'authentification
const PUBLIC_ROUTES = [
	'/',
    '/accueil',
	'/login',
    '/inscription',
    '/forgot-password',
    '/reset-password',
    '/demo',
    '/confidentialite',
    '/mentions-legales'
];

function isPublicRoute(pathname) {
    return PUBLIC_ROUTES.some(route => pathname === route || pathname.startsWith(route + '/'));
}

// ⭐ Composant qui gère l'inactivité
function AuthenticatedApp() {
    // ✅ 1. TOUS LES HOOKS EN PREMIER (toujours appelés)
    const { isIdle, resetTimer } = useIdleTimer();
    const { logout, token } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // ✅ 2. useEffect pour les interactions utilisateur
    useEffect(() => {
        const handleUserActivity = () => {
            if (isIdle) {
                resetTimer();
            }
        };

        window.addEventListener('click', handleUserActivity);
        window.addEventListener('keydown', handleUserActivity);
        window.addEventListener('mousemove', handleUserActivity);

        return () => {
            window.removeEventListener('click', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
            window.removeEventListener('mousemove', handleUserActivity);
        };
    }, [isIdle, resetTimer]);

    // ✅ 3. Gestion de l'inactivité
    useEffect(() => {
        if (isIdle) {
            console.log('Session inactive - Déconnexion automatique');
            logout();
            navigate('/login');
        }
    }, [isIdle, logout, navigate]);

    // ✅ 4. Vérification du token
    useEffect(() => {
        const currentPath = location.pathname;
        
        // Si l'utilisateur n'est pas authentifié ET qu'il n'est pas sur une route publique
        if (!token && !isPublicRoute(currentPath)) {
            console.log('🔒 Non authentifié - Redirection vers login');
            navigate('/login');
        }
        
        // Si l'utilisateur est authentifié ET qu'il est sur une route publique (sauf login)
        if (token && isPublicRoute(currentPath) && currentPath !== '/login') {
            console.log('✅ Déjà authentifié - Redirection vers dashboard');
            navigate('/dashboard');
        }
    }, [token, navigate, location.pathname]);

    return (
        <>
            <SessionExpiredModal 
                isIdle={isIdle} 
                onReconnect={() => {
                    resetTimer();
                }}
            />
            <AppRoutes />
        </>
    );
}

// ⭐ Wrapper avec IdleTimerProvider (5 minutes)
function AppWithIdleTimer() {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const handleSessionTimeout = useCallback(() => {
        console.log('Timeout session - Déconnexion');
        logout();
        navigate('/login');
    }, [logout, navigate]);

    return (
        <IdleTimerProvider timeoutMinutes={5} onTimeout={handleSessionTimeout}>
            <AuthenticatedApp />
        </IdleTimerProvider>
    );
}

// ⭐ Composant principal
export default function App() {
    return (
        <AuthProvider>
            <Router>
				<MenuProvider> 
                	<AppWithIdleTimer />
				</MenuProvider> 
            </Router>
        </AuthProvider>
    );
}